import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils.ts';

import {
  DateTime,
  parseISO8601DateTime,
  parseUnixTimestamp,
} from '../../../utils/dates';
import { createPaginatedResultSchema } from './shared/paginated';

const BaseTransaction = z.object({
  timeOfTransaction: z.number().gte(0).nullish(),
  timeOfTransactionUnix: z.number().nullish(),
  transactionTime: z.string().nullish(),
  endTimeUnix: z.number().nullish(),
  startTimeUnix: z.number().nullish(),
  createdAt: z.string().nullish(),
  startTime: z.string().nullish(),
  endTime: z.string().nullish(),
  durationMinutes: z.number(),
  payInAdvance: z.boolean(),
  parkingFee: z.number(),
  processingFee: z.number(),
  convenienceFee: z.number(),
  taxAmount: z.number(),
  total: z.number(),
  paymentId: z.string(),
  paymentIntent: z.string(),
  sessionId: z.string().optional(),
  refundId: z.string().optional(),
  refundStatus: z.string().optional(),
  refundCreatedAt: zodTimeStamp.nullish(),
});

const Transaction = BaseTransaction.transform((transaction) => {
  let createdAt: DateTime, endAt: DateTime, timeOfTransaction: DateTime;

  if (transaction.startTime) {
    createdAt = parseISO8601DateTime(transaction.startTime);
  } else if (transaction.startTimeUnix) {
    // TODO: remove this one after all the data is migrated
    createdAt = parseUnixTimestamp(transaction.startTimeUnix);
  } else {
    createdAt = parseISO8601DateTime('');
  }

  if (transaction.endTime) {
    endAt = parseISO8601DateTime(transaction.endTime);
  } else if (transaction.endTimeUnix) {
    // TODO: remove this one after all the data is migrated
    endAt = parseUnixTimestamp(transaction.endTimeUnix);
  } else {
    endAt = parseISO8601DateTime('');
  }

  if (transaction.transactionTime) {
    timeOfTransaction = parseISO8601DateTime(transaction.transactionTime);
  } else if (
    transaction.timeOfTransactionUnix ||
    transaction.timeOfTransaction
  ) {
    // TODO: remove this one after all the data is migrated
    timeOfTransaction = parseUnixTimestamp(
      (transaction.timeOfTransactionUnix ||
        transaction.timeOfTransaction) as number
    );
  } else {
    timeOfTransaction = parseISO8601DateTime('');
  }

  return {
    ...transaction,
    createdAt: createdAt,
    endAt: endAt,
    timeOfTransaction,
    startTime: createdAt,
  };
});

export enum PaymentStatus {
  PAID = 'PAID',
  PREAUTHORIZED = 'PREAUTHORIZED',
  OPEN = 'OPEN',
  DISPUTED = 'DISPUTED',
  CANCELED = 'CANCELED',
}

const TransactionExtended = BaseTransaction.extend({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string(),
  phone: z.string().optional(),
  locationId: z.string(),
  reservationId: z.string(),
  plate: z.string(),
  plateState: z.string(),
  vehicleMake: z.string().optional(),
  vehicleModel: z.string().optional(),
  vehicleColor: z.string().optional(),
  smsOptIn: z.boolean(),
  paymentStatus: z.nativeEnum(PaymentStatus).optional(),
  status: z.string(),
  refundableAmount: z.number().optional(),
  isRefunded: z.boolean(),
  refundAmount: z.number(),
  disputeId: z.string().optional(),
  // API may send cents as a number (transactions) or `{ value, currency }` (invoices).
  disputeAmount: z
    .union([
      z.number(),
      z.object({ value: z.number() }).transform((amount) => amount.value),
    ])
    .optional(),
  disputeAmountCurrency: z.string().optional(),
  disputeCreatedAt: zodTimeStamp.nullish(),
  disputeStatus: z.string().optional(),
  disputeReason: z.string().optional(),
  disputeRespondDueBy: zodTimeStamp.nullish(),
  /** How the disputed charge was originated when not a normal reservation payment. */
  disputeSource: z
    .enum([
      'reservation_payment',
      'violation_invoice',
      'monthly_parking_subscription',
    ])
    .optional(),
  invoiceId: z.string().optional(),
  violationId: z.string().optional(),
  agreementId: z.string().optional(),
  billingPeriodStart: zodTimeStamp.nullish(),
  billingPeriodEnd: zodTimeStamp.nullish(),
}).transform((transaction) => {
  let createdAt: DateTime, endAt: DateTime, timeOfTransaction: DateTime;

  if (transaction.startTime) {
    createdAt = parseISO8601DateTime(transaction.startTime);
  } else if (transaction.startTimeUnix) {
    // TODO: remove this one after all the data is migrated
    createdAt = parseUnixTimestamp(transaction.startTimeUnix);
  } else {
    createdAt = parseISO8601DateTime('');
  }

  if (transaction.endTime) {
    endAt = parseISO8601DateTime(transaction.endTime);
  } else if (transaction.endTimeUnix) {
    // TODO: remove this one after all the data is migrated
    endAt = parseUnixTimestamp(transaction.endTimeUnix);
  } else {
    endAt = parseISO8601DateTime('');
  }

  if (transaction.transactionTime) {
    timeOfTransaction = parseISO8601DateTime(transaction.transactionTime);
  } else if (
    transaction.timeOfTransactionUnix ||
    transaction.timeOfTransaction
  ) {
    // TODO: remove this one after all the data is migrated
    timeOfTransaction = parseUnixTimestamp(
      (transaction.timeOfTransactionUnix ||
        transaction.timeOfTransaction) as number
    );
  } else {
    timeOfTransaction = parseISO8601DateTime('');
  }

  return {
    ...transaction,
    createdAt: createdAt,
    endAt: endAt,
    endTime: endAt,
    timeOfTransaction,
    startTime: createdAt,
  };
});

export const SentMessageStatus = z.enum([
  'sending',
  'delivered',
  'failed',
  'unsubscribed',
]);
export type SentMessageStatusType = z.infer<typeof SentMessageStatus>;

export const Transport = z.enum(['email', 'sms']);
export type TransportType = z.infer<typeof Transport>;

export const Sender = z.object({
  name: z.string().optional(),
  source: z.string().optional(),
});
export type SenderType = z.infer<typeof Sender>;

export const Recipient = z.object({
  name: z.string().optional(),
  target: z.string(),
});
export type RecipientType = z.infer<typeof Recipient>;

export const StaticContent = z.object({
  subject: z.string().optional(),
  body: z.string().optional(),
});
export type StaticContentType = z.infer<typeof StaticContent>;

export const DynamicContent = z.object({
  template_name: z.string().optional(),
  variables: z.record(z.string(), z.any()).optional(),
  location_id: z.string().nullish(),
});
export type DynamicContentType = z.infer<typeof DynamicContent>;

export const Content = z.object({
  static: StaticContent.nullish(),
  dynamic: DynamicContent.nullish(),
});
export type ContentType = z.infer<typeof Content>;

export const Disposition = z.enum(['inline', 'attachment']);
export type DispositionType = z.infer<typeof Disposition>;

export const Attachment = z.object({
  content: z.string(),
  type: z.string(),
  filename: z.string(),
  disposition: Disposition,
  content_id: z.string().optional(),
});
export type AttachmentType = z.infer<typeof Attachment>;

export const Communication = z
  .object({
    message_id: z.string().trim().min(1, { message: 'Message ID is required' }),
    external_id: z.string().nullable(),
    reference_id: z.string(),
    status: SentMessageStatus,
    transport: Transport,
    sender: Sender,
    recipient: Recipient,
    content: Content,
    attachments: z.array(Attachment).nullish(),
    created_at: z.string().min(1, { message: 'Created At is required' }),
    updated_at: z.string().min(1, { message: 'Updated At is required' }),
  })
  .transform((communication) => ({
    ...communication,
    messageId: communication.message_id,
    externalId: communication.external_id,
    referenceId: communication.reference_id,
    createdAt: communication.created_at,
    updatedAt: communication.updated_at,
  }));

export const Reservation = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string(),
    phone: z.string().optional(),
    locationId: z.string(),
    reservationId: z.string(),
    transactions: z.array(Transaction),
    endTime: z.string().nullish(),
    endTimeUnix: z.number().nullish(),
    plate: z.string(),
    plateState: z.string(),
    status: z.enum([
      'new',
      'newRenewed',
      'scheduled',
      'inProgress',
      'nearingExpiration',
      'expired',
      'draft',
      'refunded',
      'presold',
    ]),
    createdAt: z.string().nullish(),
    createdAtUnix: z.string().nullish(),
    smsOptIn: z.boolean(),
    communications: z.array(Communication).nullable().optional(),
    plateLevenstianDistance: z.number().optional(),
  })
  .transform((reservation) => {
    let createdAt, endAt;

    if (reservation.createdAt) {
      createdAt = parseISO8601DateTime(reservation.createdAt);
    } else if (reservation.createdAtUnix) {
      createdAt = parseISO8601DateTime(reservation.createdAtUnix);
    } else {
      // invalid date
      createdAt = parseISO8601DateTime('');
    }

    if (reservation.endTime) {
      endAt = parseISO8601DateTime(reservation.endTime);
    } else if (reservation.endTimeUnix) {
      endAt = parseUnixTimestamp(reservation.endTimeUnix);
    } else {
      // invalid date
      endAt = parseISO8601DateTime('');
    }

    return {
      ...reservation,
      endAt,
      endTime: endAt,
      startTime: createdAt,
      createdAt: createdAt,
    };
  });

export const ReservationUpdateSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string(),
  phone: z.string().optional(),
  locationId: z.string(),
  reservationId: z.string(),
  endTime: z.string().nullish(),
  endTimeUnix: z.number().nullish(),
  plate: z.string(),
  plateState: z.string(),
  status: z.enum([
    'new',
    'newRenewed',
    'scheduled',
    'inProgress',
    'nearingExpiration',
    'expired',
    'draft',
    'refunded',
    'presold',
  ]),
  createdAt: z.string().nullish(),
  createdAtUnix: z.string().nullish(),
});

export enum ReservationStatus {
  new = 'New',
  newRenewed = 'New Renewed',
  scheduled = 'Scheduled',
  inProgress = 'In Progress',
  nearingExpiration = 'Nearing Expiration',
  expired = 'Expired',
  draft = 'Draft',
  refunded = 'Refunded',
  presold = 'Presold',
}

export type TransactionType = z.infer<typeof TransactionExtended>;
export const TransactionList = z.array(TransactionExtended);
export type TransactionListType = z.infer<typeof TransactionList>;

export type TransactionBaseType = z.infer<typeof Transaction>;
export const TransactionBaseList = z.array(Transaction);
export type TransactionBaseListType = z.infer<typeof TransactionBaseList>;

export type ReservationType = z.infer<typeof Reservation>;
export const ReservationList = z.array(Reservation);
export type ReservationListType = z.infer<typeof ReservationList>;

export type ReservationUpdateSchemaType = z.infer<
  typeof ReservationUpdateSchema
>;

export type CommunicationType = z.infer<typeof Communication>;
export const CommunicationList = z.array(Communication);
export type CommunicationListType = z.infer<typeof CommunicationList>;

// Paginated transactions
const TransactionPaginationKeySchema = z.object({
  reservationId: z.string(),
  createdAt: z.string(),
});

export type TransactionPaginationKey = z.infer<
  typeof TransactionPaginationKeySchema
>;

export const TransactionsPaginatedResult = createPaginatedResultSchema(
  TransactionExtended,
  TransactionPaginationKeySchema
);

export type TransactionsPaginatedResultType = z.infer<
  typeof TransactionsPaginatedResult
>;

// GET /reservations cursor. Mirrors reservation.ReservationStartKey on the
// Go side: (createdAtMillis, reservationId) for stable keyset pagination.
export const ReservationsPaginationKeySchema = z.object({
  createdAtMillis: z.number(),
  reservationId: z.string(),
});
export type ReservationsPaginationKey = z.infer<
  typeof ReservationsPaginationKeySchema
>;

export const ReservationsPaginatedResult = createPaginatedResultSchema(
  Reservation,
  ReservationsPaginationKeySchema
);
export type ReservationsPaginatedResultType = z.infer<
  typeof ReservationsPaginatedResult
>;
